import React, { useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import CustomSizeDialog from "./CustomSizeDialog";
import BadgeSizeLabel from "./BadgeSizeLabel";
import { getCompatibleAveryTemplates } from "../helpers/averyTemplates";

/** Preset badge sizes matching UK/EU Avery sheet labels (same mm as `label` on those templates). */
const euIconDims = (wMm, hMm) => {
  const maxSide = 40;
  if (wMm >= hMm) {
    return { iconWidth: maxSide, iconHeight: (maxSide * hMm) / wMm };
  }
  return { iconWidth: (maxSide * wMm) / hMm, iconHeight: maxSide };
};

const EU_BADGE_SIZE_PRESETS = [
  {
    id: "eu-80x50",
    ...euIconDims(80, 50),
    widthInMm: 80,
    heightInMm: 50,
    width: 80,
    height: 50,
    displaySizeInMm: "80mm × 50mm",
    displayMmOnly: true,
  },
  {
    id: "eu-86x55",
    ...euIconDims(86, 55),
    widthInMm: 86,
    heightInMm: 55,
    width: 86,
    height: 55,
    displaySizeInMm: "86mm × 55mm",
    displayMmOnly: true,
  },
  {
    id: "eu-90x54",
    ...euIconDims(90, 54),
    widthInMm: 90,
    heightInMm: 54,
    width: 90,
    height: 54,
    displaySizeInMm: "90mm × 54mm",
    displayMmOnly: true,
  },
  {
    id: "eu-90x60",
    ...euIconDims(90, 60),
    widthInMm: 90,
    heightInMm: 60,
    width: 90,
    height: 60,
    displaySizeInMm: "90mm × 60mm",
    displayMmOnly: true,
  },
  {
    id: "eu-85x54",
    ...euIconDims(85, 54),
    widthInMm: 85,
    heightInMm: 54,
    width: 85,
    height: 54,
    displaySizeInMm: "85mm × 54mm",
    displayMmOnly: true,
  },
];

export const SIZES = [
  {
    id: 1,
    displayWidth: '4',
    displayHeight: '3',
    displaySizeInMm: "101mm × 76mm",
    widthInMm: 101.6,
    heightInMm: 76.2,
    width: 4,
    height: 3,
    iconWidth: 40,
    iconHeight: 30,
  },
  {
    id: 2,
    displayWidth: '4',
    displayHeight: '6',
    displaySizeInMm: "101mm × 152mm",
    widthInMm: 101.6,
    heightInMm: 152.4,
    width: 4,
    height: 6,
    iconWidth: 30,
    iconHeight: 40,
  },
  // Avery 5361/5362 printable cell (template_cell), not finished laminate label size
  {
    id: 3,
    displayWidth: '3',
    displayWidthFraction: '1/4',
    displayHeight: '2',
    displaySizeInMm: "82.55mm × 50.8mm",
    widthInMm: 82.55,
    heightInMm: 50.8,
    width: 3.25,
    height: 2,
    iconWidth: 40,
    iconHeight: (40 * 50.8) / 82.55,
  },
  {
    id: 4,
    displayWidth: '3',
    displayWidthFraction: '1/2',
    displayHeight: '2',
    displayHeightFraction: '1/4',
    displaySizeInMm: "89mm × 57mm",
    widthInMm: 88.9,
    heightInMm: 57.15,
    width: 3.5,
    height: 2.25,
    iconWidth: 38.5,
    iconHeight: 24.75,
  },
  {
    id: 5,
    displayWidth: '3',
    displayWidthFraction: '3/8',
    displayHeight: '2',
    displayHeightFraction: '1/3',
    displaySizeInMm: "86mm × 59mm",
    widthInMm: 85.725,
    heightInMm: 59.2667,
    width: 3.375,
    height: 2.3333,
    iconWidth: 37.125,
    iconHeight: 25.6663,
  },

];

/** Scales preset iconWidth/iconHeight to CSS pixels (smaller than 1 keeps tiles less crowded). */
const TILE_ICON_BASE_SCALE = 1.1;
/** Extra shrink when badge is portrait (taller than wide) so it fits the tile. */
const PORTRAIT_TILE_ICON_SCALE = 0.82;

const renderSizeTile = (size, onPick) => {
  const {
    id,
    displayWidth,
    displayWidthFraction,
    displayHeight,
    displayHeightFraction,
    displaySizeInMm,
    displayMmOnly,
    iconWidth,
    iconHeight,
    ...badgeSize
  } = size;
  const compatibleTemplates = getCompatibleAveryTemplates(badgeSize);
  const aliases = compatibleTemplates.flatMap(({ aliases: templateAliases }) => templateAliases);

  const labelSizeProps = displayMmOnly
    ? size
    : { displayWidth, displayWidthFraction, displayHeight, displayHeightFraction };

  const portraitIconScale =
    size.heightInMm > size.widthInMm ? PORTRAIT_TILE_ICON_SCALE : 1;
  const iconPxScale = TILE_ICON_BASE_SCALE * portraitIconScale;

  return (
    <div key={id} className="size-tile-option">
      <button type="button" className="size-tile" onClick={() => onPick(size)}>
        <div className="size-tile-main">
          <div className="size-tile-icon-container">
            <div
              className="size-tile-icon"
              style={{
                "--size-tile-icon-width": `${iconWidth * iconPxScale}px`,
                "--size-tile-icon-height": `${iconHeight * iconPxScale}px`,
              }}
            />
          </div>
          <div className="size-tile-label">
            <span className="size-tile-size">
              <BadgeSizeLabel size={labelSizeProps} />
            </span>
          </div>
          {!displayMmOnly && <div className="size-tile-mm">{displaySizeInMm}</div>}
        </div>
        <div
          className={
            aliases.length > 0
              ? "size-tile-avery-aliases"
              : "size-tile-avery-aliases size-tile-avery-aliases--empty"
          }
          aria-hidden={aliases.length === 0}
        >
          {aliases.length > 0 ? `Avery: ${aliases.join(", ")}` : null}
        </div>
      </button>
    </div>
  );
};

const SizeDialog = ({ open, onContinue, onClose, useInitialOverlay = true }) => {
  const [openCustomSizePopup, setOpenCustomSizePopup] = useState(false);
  const dialogClasses = useInitialOverlay
    ? { root: "initial-dialog-overlay" }
    : undefined;
  const handleClose = () => {
    setOpenCustomSizePopup(false);
    onClose?.();
  };

  return (
    <>
      <Dialog
        disableEnforceFocus
        className="size-dialog"
        classes={dialogClasses}
        onClose={onClose ? handleClose : undefined}
        open={open}
      >
        <DialogContent>
          {onClose && (
            <button
              aria-label="Close badge size picker"
              className="button size-dialog-close"
              onClick={handleClose}
              type="button"
            >
              <CloseIcon className="button-icon" />
            </button>
          )}
          <h3 className="dialog-title">Choose badge size</h3>
          <div className="size-tiles">
            <div className="size-tiles-row">
              {SIZES.map((size) => renderSizeTile(size, onContinue))}
            </div>
            <div className="size-tiles-row">
              {EU_BADGE_SIZE_PRESETS.map((size) => renderSizeTile(size, onContinue))}
            </div>
            <div className="size-tiles-row size-tiles-row-other">
              <button
                type="button"
                className="size-tile-custom"
                onClick={() => setOpenCustomSizePopup(true)}
              >
                <span className="size-tile-custom-title">Custom badge size</span>
                <span className="size-tile-custom-hint">Define a width and height in millimetres</span>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CustomSizeDialog
        open={openCustomSizePopup}
        onClose={() => setOpenCustomSizePopup(false)}
        onContinue={(customSize) => onContinue(customSize)}
      />
    </>
  );
};

export default SizeDialog;
